import React from 'react';
import { ArrowRight, Check } from 'lucide-react';
import { CHECKOUT_URL, pricing } from '../data/content';
import { trackPixel } from '../utils/metaPixel';
export function scrollToOffer() {
  const el = document.getElementById('offer-card') ?? document.getElementById('angebot');
  if (!el) return;
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  el.scrollIntoView({
    behavior: reduce ? 'auto' : 'smooth',
    block: 'start'
  });
}

/**
 * Green button like the model page. to='offer' scrolls to the offer card (no pixel event);
 * to='checkout' is ONLY used by the offer card button and fires InitiateCheckout.
 */
export function CtaButton({
  label,
  to,
  className = ''
}: {
  label: string;
  to: 'offer' | 'checkout';
  className?: string;
}) {
  const isCheckout = to === 'checkout';
  function handleClick(e: React.MouseEvent<HTMLAnchorElement>) {
    if (!isCheckout) {
      e.preventDefault();
      scrollToOffer();
      return;
    }
    trackPixel('InitiateCheckout', {
      value: pricing.value,
      currency: pricing.currency
    });
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
    e.preventDefault();
    window.setTimeout(() => {
      window.location.href = CHECKOUT_URL;
    }, 180);
  }
  return <a href={isCheckout ? CHECKOUT_URL : '#offer-card'} onClick={handleClick} data-cta={isCheckout ? 'checkout' : 'offer'} className={`btn-verde group flex w-full items-center justify-center gap-3 rounded-[14px] px-6 py-[18px] text-center text-[17px] font-extrabold uppercase leading-snug tracking-[0.02em] text-white shadow-[0_14px_28px_rgba(17,96,63,0.28)] transition-transform duration-150 ease-out active:translate-y-[2px] focus:outline-none focus-visible:ring-4 focus-visible:ring-verde/40 sm:mx-auto sm:max-w-md ${className}`} data-id="element-173" data-mp="3hbqcanf3odhp">
      <span data-id="element-174" data-mp="25xuvla8jrwod">{label}</span>
      <ArrowRight className="h-5 w-5 shrink-0 transition-transform duration-150 group-hover:translate-x-0.5" strokeWidth={2.75} aria-hidden="true" data-id="element-175" data-mp="25w9x59j43tl8" />
    </a>;
}

/** Title with ==red highlight== marks */
export function Rich({
  text,
  light = false
}: {
  text: string;
  light?: boolean;
}) {
  const parts = text.split(/(==[^=]+==)/g).filter(Boolean);
  return <>
      {parts.map((p, i) => p.startsWith('==') ? <span key={i} className={light ? 'text-[#FFB4AE]' : 'text-rosso'} data-id="element-176" data-mp="27mzp72br91cg">
            {p.slice(2, -2)}
          </span> : <React.Fragment key={i} data-id="element-177" data-mp="1jyoxopry8e0u">{p}</React.Fragment>)}
    </>;
}
export function H2({
  text,
  light = false,
  className = ''
}: {
  text: string;
  light?: boolean;
  className?: string;
}) {
  return <h2 className={`mx-auto max-w-3xl text-center font-serif text-[29px] font-black leading-[1.15] tracking-[-0.01em] sm:text-[40px] ${light ? 'text-white' : 'text-ink'} ${className}`} data-id="element-178" data-mp="1txeesgc0tug1">
      <Rich text={text} light={light} data-id="element-179" data-mp="359yt9y4s1bkd" />
    </h2>;
}
export function Kicker({
  children,
  light = false
}: {
  children: React.ReactNode;
  light?: boolean;
}) {
  return <p className={`mx-auto mb-3 w-fit text-center text-[13px] font-extrabold uppercase tracking-[0.14em] ${light ? 'text-[#9FD9B8]' : 'text-verde'}`} data-id="element-180" data-mp="m3w8qi7hmu4v">{children}</p>;
}
export function Sub({
  children,
  light = false
}: {
  children: React.ReactNode;
  light?: boolean;
}) {
  return <p className={`mx-auto mt-3 max-w-2xl text-center text-[16.5px] leading-relaxed ${light ? 'text-white/80' : 'text-ink-soft'}`} data-id="element-181" data-mp="i2sycb87lqb1">{children}</p>;
}
export function GreenCheck({
  className = ''
}: {
  className?: string;
}) {
  return <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-verde-light ${className}`} data-id="element-182" data-mp="19sxchs5dlh3t">
      <Check className="h-4 w-4 text-verde" strokeWidth={3.25} aria-hidden="true" data-id="element-183" data-mp="w1shj3cydczt" />
    </span>;
}

/** Image or a neutral placeholder box while the real image is not ready */
export function Img({
  src,
  alt,
  className = '',
  ratio = 'aspect-[2/3]',
  eager = false
}: {
  src: string;
  alt: string;
  className?: string;
  ratio?: string;
  eager?: boolean;
}) {
  if (!src) {
    return <div className={`${ratio} flex items-center justify-center rounded-2xl border-2 border-dashed border-current/30 bg-white/40 p-3 text-center text-xs opacity-60 ${className}`} aria-label={alt} data-id="element-184" data-mp="4x7t9ynv0r3p">
        {alt}
      </div>;
  }
  // The ratio also reserves the height of real images: lazy images loading during the smooth scroll must not push the offer card down
  const fit = className.includes('object-') ? '' : 'object-contain';
  return <img src={src} alt={alt} loading={eager ? 'eager' : 'lazy'} decoding="async" className={`${ratio} ${fit} ${className}`} data-id="element-185" data-mp="noqtxyuevt2k" />;
}
export function Section({
  id,
  tone,
  children,
  className = ''
}: {
  id?: string;
  tone: 'cream' | 'cream2' | 'dark';
  children: React.ReactNode;
  className?: string;
}) {
  const bg = {
    cream: 'bg-cream text-ink',
    cream2: 'bg-cream-2 text-ink',
    dark: 'bg-band text-white'
  }[tone];
  return <section id={id} className={`${bg} ${className}`} data-id="element-186" data-mp="12jx2asgzc3z2">
      <div className="mx-auto max-w-5xl px-5 py-12 sm:px-8 sm:py-16" data-id="element-187" data-mp="12j57hjv7ia9u">{children}</div>
    </section>;
}